import { gql, useMutation, useQuery } from '@apollo/client';
import { Box, Button, Link, Stack, useToast } from '@chakra-ui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  AdminPage,
  AdminPageContent,
  AdminPageHeader,
  AdminPageHeading,
} from '@/components/AdminPage';
import {
  EOnlinePlatformType,
  EventVenueType,
} from '@/graphql-types/globalTypes';
import { AdminTopBarNavMenuFrame } from '@/imports/ui/admin/layouts/AdminTopBarNavMenuFrame';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { ChevronLeftIcon } from '@/imports/ui/chakra/feather';
import { AboutBoothCard } from '@/modules/events/components/About/AboutBoothCard';
import { VideoSettingsBoothCard } from '@/modules/events/components/VideoSettings/VideoSettingsBoothCard';
import { MAX_PARTICIPANTS } from '@/modules/events/components/VideoSettings/constants';
import { ConferenceBoothFormValues } from '@/modules/events/types';
import { EVideoStreamType } from '@/modules/events/utilities';

import {
  ConferenceBoothCreationPage_CreateConferenceBooth,
  ConferenceBoothCreationPage_CreateConferenceBoothVariables,
} from './graphql-types/ConferenceBoothCreationPage_CreateConferenceBooth';
import { ConferenceBoothCreationPage_CurrentUser } from './graphql-types/ConferenceBoothCreationPage_CurrentUser';
import {
  ConferenceBoothCreationPage_Event,
  ConferenceBoothCreationPage_EventVariables,
} from './graphql-types/ConferenceBoothCreationPage_Event';
import {
  ConferenceBoothCreationPage_Organization,
  ConferenceBoothCreationPage_OrganizationVariables,
} from './graphql-types/ConferenceBoothCreationPage_Organization';

const GET_USER = gql`
  query ConferenceBoothCreationPage_CurrentUser {
    currentUser {
      _id
      formDefaults {
        events {
          dialInEnabled
          numParticipants
        }
      }
    }
  }
`;

const EVENT = gql`
  query ConferenceBoothCreationPage_Event($eventId: String!) {
    event(input: { _id: $eventId }) {
      eventV2 {
        _id
        name
        endDate
      }
    }
  }
`;

const ORGANIZATION = gql`
  query ConferenceBoothCreationPage_Organization(
    $input: SingleOrganizationInput!
  ) {
    organization(input: $input) {
      result {
        _id
        displayName
      }
    }
  }
`;

const CREATE_BOOTH = gql`
  mutation ConferenceBoothCreationPage_CreateConferenceBooth(
    $input: CreateConferenceBoothInput!
  ) {
    createConferenceBooth(input: $input) {
      result {
        _id
      }
    }
  }
`;

export const ConferenceBoothCreationPage: React.FC = () => {
  const toast = useToast();
  const router = useRouter();
  const eventId = router.query.eventId.toString();
  const organizationId = router.query.organizationId.toString() ?? '';

  const userQuery = useQuery<ConferenceBoothCreationPage_CurrentUser>(GET_USER);

  const eventQuery = useQuery<
    ConferenceBoothCreationPage_Event,
    ConferenceBoothCreationPage_EventVariables
  >(EVENT, {
    variables: {
      eventId,
    },
  });

  const organizationQuery = useQuery<
    ConferenceBoothCreationPage_Organization,
    ConferenceBoothCreationPage_OrganizationVariables
  >(ORGANIZATION, {
    variables: {
      input: {
        id: organizationId,
      },
    },
  });

  const [createConferenceBooth] = useMutation<
    ConferenceBoothCreationPage_CreateConferenceBooth,
    ConferenceBoothCreationPage_CreateConferenceBoothVariables
  >(CREATE_BOOTH, {
    update(cache) {
      const normalizedId = cache.identify({
        id: eventId,
        __typename: 'ConferenceEventV2',
      });
      cache.evict({ id: normalizedId, fieldName: 'conferenceBooths' });
      cache.gc();
    },
  });

  const defaultValues: ConferenceBoothFormValues = React.useMemo(() => {
    return {
      videoStreamType: EVideoStreamType.EXTERNAL,
      externalPlatformMeetingUrl: null,
      externalPlatformAdditionalDetails: null,
      description: '',
      isLiveVideoStreamDialInEnabled:
        userQuery.data?.currentUser?.formDefaults.events.dialInEnabled ?? true,
      audienceParticipationCapacity:
        userQuery.data?.currentUser?.formDefaults.events.numParticipants ??
        MAX_PARTICIPANTS,
      moderators: [],
      title: '',
      body: '',
      callToAction: '',
      url: '',
      image: {
        imageId: '',
      },
      venueType: EventVenueType.ONLINE,
      externalPlatformName: null,
      address: {
        administrativeArea: null,
        country: null,
        locality: null,
        name: null,
        postalCode: null,
        streetAddress: null,
        streetAddressLine2: null,
      },
      inPersonDescription: null,
      registerInterestTitle: '',
      isDefaultAdvertisementEnabled: false,
    };
  }, [
    userQuery.data?.currentUser?.formDefaults.events.dialInEnabled,
    userQuery.data?.currentUser?.formDefaults.events.numParticipants,
  ]);

  const methods = useForm<ConferenceBoothFormValues>({
    defaultValues,
  });

  if (eventQuery.data?.event.eventV2?.__typename === 'SimpleEventV2') {
    throw new Error('Unexpected event spec');
  }

  const conferenceEventId = eventQuery.data?.event.eventV2?._id || '';
  const conferenceEventEndDate = eventQuery.data?.event.eventV2?.endDate;

  const onSubmit = methods.handleSubmit(async (data) => {
    if (!data.venueType) {
      throw new Error('Unexpected Missing Venue Type');
    }

    try {
      const freshBooth = await createConferenceBooth({
        variables: {
          input: {
            description: data.description,
            moderators:
              data.videoStreamType === EVideoStreamType.EXTERNAL
                ? null
                : data.moderators.map((moderator) => ({
                    existingUserId: moderator._id,
                    isHidden: moderator.isHidden,
                  })),
            conferenceEventId,
            hostOrganizationId: organizationId,
            venue: {
              type: data.venueType,
              inPersonLocation:
                data.venueType === EventVenueType.IN_PERSON ||
                data.venueType === EventVenueType.HYBRID
                  ? {
                      address: {
                        administrativeArea: data.address.administrativeArea,
                        country: data.address.country,
                        locality: data.address.locality,
                        name: data.address.name,
                        postalCode: data.address.postalCode,
                        streetAddress: data.address.streetAddress,
                        streetAddressLine2: data.address.streetAddressLine2,
                      },
                      description: data.inPersonDescription,
                    }
                  : undefined,
              online:
                data.venueType === EventVenueType.ONLINE ||
                data.venueType === EventVenueType.HYBRID
                  ? {
                      type: (() => {
                        if (!data.videoStreamType) {
                          throw new Error(
                            'Unexpected Missing Video Stream Type'
                          );
                        }

                        return {
                          [EVideoStreamType.EXTERNAL]:
                            EOnlinePlatformType.EXTERNAL,
                          [EVideoStreamType.PRE_RECORDED_VIDEO]:
                            EOnlinePlatformType.TRADEWING,
                          [EVideoStreamType.AGORA_VIDEO_CALL]:
                            EOnlinePlatformType.TRADEWING,
                        }[data.videoStreamType];
                      })(),
                      externalPlatform:
                        data.videoStreamType === EVideoStreamType.EXTERNAL
                          ? {
                              additionalDetails:
                                data.externalPlatformAdditionalDetails,
                              url: data.externalPlatformMeetingUrl,
                            }
                          : undefined,
                      tradewingPlatform: undefined,
                    }
                  : undefined,
            },
          },
        },
      });

      const boothId = freshBooth.data?.createConferenceBooth.result?._id;

      router.push(`/admin/conference/${eventId}/booth/${boothId}`);

      toast({
        status: 'success',
        description: 'You successfully created a booth',
      });
    } catch {
      toast({ status: 'error', description: 'An unexpected error occurred' });
    }
  });

  const isSubmitting = methods.formState.isSubmitting;
  const videoStreamType = methods.watch('videoStreamType');

  return (
    <AdminTopBarNavMenuFrame>
      {eventQuery.loading || organizationQuery.loading ? (
        <Loading />
      ) : eventQuery.error || organizationQuery.error ? (
        <UnexpectedError />
      ) : !eventQuery.data?.event.eventV2 ||
        !organizationQuery.data?.organization?.result ? (
        <UnexpectedError />
      ) : (
        <AdminPage w="100%">
          <AdminPageHeader px="6" pt="4">
            <Stack spacing="4" alignItems="flex-start">
              <Link
                as={NextLink}
                href={`/admin/conference/${eventId}/expo`}
                display="flex"
                alignItems="center"
                gap="2"
              >
                <ChevronLeftIcon />
                <span>Back to {eventQuery.data.event.eventV2.name}</span>
              </Link>
              <AdminPageHeading>Create Booth</AdminPageHeading>
            </Stack>
          </AdminPageHeader>
          <AdminPageContent px="6">
            <FormProvider {...methods}>
              <Stack spacing="6">
                {/* Row 1: About Booth + Video Settings */}
                <Box
                  display="grid"
                  // gridTemplateColumns={{ base: '1fr', lg: '1fr 1fr' }}
                  gap="6"
                >
                  <AboutBoothCard
                    isEditing
                    sponsorName={
                      organizationQuery.data.organization.result.displayName
                    }
                  />
                  <VideoSettingsBoothCard
                    isEditing
                    entityId={{
                      conferenceBoothId: null,
                      conferenceEventId: eventId,
                    }}
                    endDate={
                      conferenceEventEndDate
                        ? new Date(conferenceEventEndDate)
                        : new Date()
                    }
                  />
                </Box>

                {/* Action buttons */}
                <Stack direction="row" justifyContent="flex-end">
                  <NextLink href={`/admin/conference/${eventId}/expo`}>
                    <Button
                      variant="outline"
                      colorScheme="gray"
                      isDisabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  </NextLink>
                  <Button onClick={onSubmit} isLoading={isSubmitting}>
                    Create Booth
                  </Button>
                </Stack>
              </Stack>
            </FormProvider>
          </AdminPageContent>
        </AdminPage>
      )}
    </AdminTopBarNavMenuFrame>
  );
};
